import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { CheckCircle, Schedule } from "@material-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GET_SERVICE_REQUEST } from "../../../../graphql/serviceRequests/getServiceRequestsQuery";
import { CONSUMER_ACCEPTS_SCHEDULED_SERVICE } from "../../../../graphql/serviceRequests/consumerScheduledServiceMutations";
import { IAppState } from "../../../../store";
import { getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { DATE_TYPE } from "../../../utils";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import {
  canReviewScheduledService,
  getAppointmentIso,
  getVehicleLabel,
} from "./scheduledServiceUtils";
import ScheduledServiceJobSheet from "./ScheduledServiceJobSheet";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(12),
    backgroundColor: "#f5f5f5",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  backButton: {
    marginRight: theme.spacing(2),
    textTransform: "none",
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 8,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: 12,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  lockedHint: {
    fontSize: 11,
    color: "#999",
    marginBottom: 4,
  },
  actionsCard: {
    position: "sticky",
    bottom: 0,
    marginTop: theme.spacing(2),
    borderRadius: 8,
    boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
  },
  actionRow: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  approveBtn: {
    flex: 1,
    backgroundColor: "#1976d2",
    color: "#fff",
    padding: theme.spacing(1.5, 2),
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  changesBtn: {
    flex: 1,
    borderColor: "#1976d2",
    color: "#1976d2",
    padding: theme.spacing(1.5, 2),
    fontWeight: 600,
    textTransform: "none",
  },
}));

const ReviewScheduledService: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const showSnackbar = useSnackBar();
  const country = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation.address.country
  );
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);

  const { data, loading } = useQuery(GET_SERVICE_REQUEST, {
    variables: { serviceRequestId: id },
    fetchPolicy: "network-only",
  });

  const [approveService, { loading: approving }] = useMutation(CONSUMER_ACCEPTS_SCHEDULED_SERVICE, {
    onCompleted: () => {
      showSnackbar({
        message: t("root:approveScheduledServiceSuccess", { defaultValue: "Service approved successfully" }),
        variant: SnackBarVariant.SUCCESS,
      });
      history.push("/account/service-requests");
    },
    onError: (error) => {
      showSnackbar({ message: error.message, variant: SnackBarVariant.ERROR });
    },
  });

  const service = data?.serviceRequest;
  const vehicleService = service?.vehicleService;
  const branchTimeZone = website?.branches?.find((b: any) => b.id === service?.branchId)?.timeZone || "UTC";
  const appointmentIso = getAppointmentIso(service);
  const canReview = canReviewScheduledService(service);
  const statusLabel = (vehicleService?.status || service?.status || "Scheduled").replace(/_/g, " ");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!service) {
    return (
      <Box p={3}>
        <Typography color="error">{t("snackBar:errorLoadingServiceRequests")}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Button className={classes.backButton} onClick={() => history.push("/account/service-requests")}>
          ← {t("misc:back", { defaultValue: "Back" })}
        </Button>
        <Typography variant="h4" style={{ fontWeight: 600, flex: 1 }}>
          {t("root:serviceDetails", { defaultValue: "Service Details" })}
        </Typography>
        <Chip icon={<Schedule fontSize="small" />} label={statusLabel} color="primary" size="small" />
      </Box>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("root:vehicleAndCategory", { defaultValue: "Vehicle & category" })}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography className={classes.lockedHint}>
                {t("root:cannotModify", { defaultValue: "Cannot modify" })}
              </Typography>
              <Typography className={classes.detailLabel}>{t("root:vehicle", { defaultValue: "Vehicle" })}</Typography>
              <Typography variant="h6">{getVehicleLabel(service.vehicle)}</Typography>
              <Typography color="textSecondary">{service.vehicle?.licencePlate}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.lockedHint}>
                {t("root:cannotModify", { defaultValue: "Cannot modify" })}
              </Typography>
              <Typography className={classes.detailLabel}>
                {t("root:serviceCategory", { defaultValue: "Service category" })}
              </Typography>
              <Typography variant="h6">{service.serviceCategory || "—"}</Typography>
              <Typography color="textSecondary">
                {t("root:referenceNumber")}: {service.referenceNumber}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("root:appointmentDetails", { defaultValue: "Appointment details" })}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.detailLabel}>
                {t("root:appointmentDate", { defaultValue: "Appointment date" })}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {appointmentIso
                  ? getLocalizedDateFormat(country, appointmentIso, DATE_TYPE.CONDENSED, branchTimeZone)
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.detailLabel}>{t("root:time", { defaultValue: "Time" })}</Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {appointmentIso
                  ? getLocalizedDateFormat(country, appointmentIso, DATE_TYPE.EXPANDED, branchTimeZone).split(", ").pop()
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.detailLabel}>
                {t("root:serviceProvider", { defaultValue: "Service provider" })}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {vehicleService?.serviceProviderName || service.serviceProviderName || "—"}
              </Typography>
            </Grid>
            {vehicleService?.totalExpenses !== undefined && (
              <Grid item xs={12} sm={4}>
                <Typography className={classes.detailLabel}>
                  {t("root:expenses", { defaultValue: "Expenses" })}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  £{((vehicleService.totalExpenses || 0) / 100).toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <ScheduledServiceJobSheet service={service} />

      {canReview && (
        <Card className={classes.actionsCard}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              {t("root:scheduledServiceReviewHint", {
                defaultValue: "Please review the scheduled service. Approve if details are correct, or request changes.",
              })}
            </Typography>
            <Box className={classes.actionRow}>
              <Button
                className={classes.approveBtn}
                variant="contained"
                onClick={() => approveService({ variables: { serviceId: id } })}
                disabled={approving}
                startIcon={<CheckCircle />}
              >
                {t("root:approveService", { defaultValue: "Approve service" })}
              </Button>
              <Button
                className={classes.changesBtn}
                variant="outlined"
                onClick={() => history.push(`/account/scheduled-service/${id}/modify`)}
                disabled={approving}
              >
                {t("root:requestChanges", { defaultValue: "Request changes" })}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ReviewScheduledService;
